import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { TransactionInquiryInfoDtoIn } from '@/models/in/TransactionInquiryInfoDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';

/**
 * "בירור עסקה" - ported from the Angular client
 * (viewer-client/src/app/_components/transactions-inquiry). A donor who sees a charge on their
 * card statement is looked up by date + last 4 card digits + amount, and gets the fund the
 * donation went to. Same three inputs, same result columns and the same admin-only gating as the
 * page it replaces (see the route in App.tsx).
 */

const isFourDigits = (value: string) => /^\d{4}$/.test(value);

// The server expects the date as 'yyyyMMdd', while <input type="date"> yields 'yyyy-MM-dd'.
const toServerDate = (value: string) => value.replace(/-/g, '');

const today = () => new Date().toISOString().slice(0, 10);
const DENSITY_STORAGE_KEY = 'table.density.transactionsInquiry';

const TransactionsInquiryPage = () => {
  const { t } = useTranslation();

  const [date, setDate] = useState(today);
  const [last4digits, setLast4digits] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Empty until a valid search is submitted - useGetAuthApi is disabled while the url is empty,
  // so nothing is requested before the user asks for it.
  const [queryUrl, setQueryUrl] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const {
    data: transactions,
    isFetching,
    isError,
    error,
  } = useGetAuthApi<TransactionInquiryInfoDtoIn[]>(queryUrl);

  // The endpoint answers 404 when nothing matches, which react-query surfaces as an error; for
  // this screen that is a legitimate "no charges found" result rather than a failure.
  const notFound = isError && (error as { status?: number })?.status === 404;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!date) next.date = t('transactionsInquiry.inputs.date.required');
    if (!last4digits) next.last4digits = t('transactionsInquiry.inputs.last4digits.required');
    else if (!isFourDigits(last4digits)) next.last4digits = t('transactionsInquiry.inputs.last4digits.pattern');
    if (amount === '') next.amount = t('transactionsInquiry.inputs.amount.required');
    else if (Number(amount) < 0) next.amount = t('transactionsInquiry.inputs.amount.positive');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const params = new URLSearchParams({
      date: toServerDate(date),
      last4digits,
      amount: String(Number(amount)),
    });
    setQueryUrl(`transactionsinquiry?${params.toString()}`);
  };

  const columns: ColumnDef<TransactionInquiryInfoDtoIn>[] = useMemo(
    () => [
      {
        id: 'firstName',
        header: Col(t('transactionsInquiry.table.headers.firstName')),
        accessorKey: 'firstName',
        enableSorting: true,
        meta: { className: 'min-w-[140px]' },
      },
      {
        id: 'lastName',
        header: Col(t('transactionsInquiry.table.headers.lastName')),
        accessorKey: 'lastName',
        enableSorting: true,
        meta: { className: 'min-w-[140px]' },
      },
      {
        id: 'fundName',
        header: Col(t('transactionsInquiry.table.headers.fundName')),
        accessorKey: 'fundName',
        enableSorting: true,
        meta: { className: 'min-w-[200px]' },
      },
      {
        id: 'fundId',
        header: Col(t('transactionsInquiry.table.headers.fundNumber')),
        accessorKey: 'fundId',
        enableSorting: true,
        meta: { className: 'min-w-[120px]' },
      },
      {
        id: 'amount',
        header: Col(t('transactionsInquiry.table.headers.amount')),
        accessorKey: 'amount',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="body-sm-regular">{row.original.amount.toFixed(2)}</span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
    ],
    [t]
  );

  return (
    <div className="w-full flex flex-col gap-4">
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="body-sm-regular">{t('transactionsInquiry.inputs.date.title')}</span>
          <Input
            type="date"
            value={date}
            error={!!errors.date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="body-sm-regular">{t('transactionsInquiry.inputs.last4digits.title')}</span>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={last4digits}
            error={!!errors.last4digits}
            onChange={(e) => setLast4digits(e.target.value.replace(/\D/g, ''))}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="body-sm-regular">{t('transactionsInquiry.inputs.amount.title')}</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            error={!!errors.amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>

        <Button variant="primary" type="submit" disabled={isFetching}>
          {t(isFetching ? 'sending' : 'send')}
        </Button>
      </form>

      {Object.values(errors).length > 0 && (
        <ul className="body-sm-regular text-(--font-error) flex flex-col gap-1">
          {Object.entries(errors).map(([field, message]) => (
            <li key={field}>{message}</li>
          ))}
        </ul>
      )}

      {!!queryUrl && (
        <DataTable
          title={t('transactionsInquiry.title')}
          data={transactions ?? []}
          isLoading={isFetching}
          columns={columns}
          density={density}
          onDensityChange={setDensity}
          columnLayoutKey="transactionsInquiry"
          emptyStateContent={{
            title: t('transactionsInquiry.notFound'),
            description: notFound ? t('transactionsInquiry.notFoundDescription') : '',
          }}
          // The result set is a single lookup, so the table's own search box stays out of the way.
          searchQuery=""
          setSearchQuery={() => {}}
          customGlobalSearch={() => true}
        />
      )}
    </div>
  );
};

export default TransactionsInquiryPage;
